<template>
    <draggable-modal @close="handleClose" ref="modalRef">
        <template #header>
            Reward/Risk
        </template>
        <template #body>
            <div class="flex flex-wrap pb-3">
                <div class="w-full p-0 mt-2">
                    <small><b>Note:</b> Please enter prices using the correct ‘-’ format, or enter decimal values instead.</small>
                </div>
                <div class="w-full p-0 mt-2">
                    <label for="entry">
                        Entry Price
                        <div class="error" v-if="errors.entry">{{ errors.entry }}</div>
                        <price-input :symbol-type="symbolType" :base-code="baseCode" :symbol="symbol" @keydown="pickPoint" :class="{'is-invalid': errors.entry}" class="form-control form-input-sm-inline" ref="entryRef" id="entry" :default-value="form.entry" v-model="form.entry"></price-input>
                    </label>
                </div>
                <div class="w-full p-0 mt-2">
                    <label for="stop">
                        Stop Loss
                        <div class="error" v-if="errors.stop">{{ errors.stop }}</div>
                        <price-input :symbol-type="symbolType" :base-code="baseCode" :symbol="symbol" @keydown="pickPoint" :class="{'is-invalid': errors.stop}" class="form-control form-input-sm-inline" ref="stopRef" id="stop" :default-value="form.stop" v-model="form.stop"></price-input>
                    </label>
                </div>
                <div class="w-full p-0 mt-2">
                    <label for="target">
                        Target Price
                        <div class="error" v-if="errors.target">{{ errors.target }}</div>
                        <price-input :symbol-type="symbolType" :base-code="baseCode" :symbol="symbol" @keydown="pickPoint" :class="{'is-invalid': errors.target}" class="form-control form-input-sm-inline" ref="targetRef" id="target" :default-value="form.target" v-model="form.target"></price-input>
                    </label>
                </div>
                <div class="w-full p-0 mt-2">
                    <label for="contracts">
                        Number of Contracts
                        <input id="contracts" class="form-control form-input-sm-inline" type="text" name="contracts" v-model="form.contracts">
                    </label>
                </div>
                <div class="w-full p-0 mt-2 form-check-inline">
                    <label for="long" class="form-check-label mr-3">
                        <input class="form-check-input mr-1" id="long" type="radio" name="longOrShort" value="long" v-model="form.longOrShort" />
                        Long
                    </label>
                    <label for="short" class="form-check-label">
                        <input class="form-check-input mr-1" id="short" type="radio" name="longOrShort" value="short" v-model="form.longOrShort" />
                        Short
                    </label>
                </div>

                <div class="w-full border bg-gray-100 flex flex-wrap p-2 mt-2 text-sm">
                    <div class="w-full flex">
                        <span>
                            Reward:
                        </span>
                        <span v-if="valid" class="ml-auto" :class="getUpDownClass(reward)">
                            ${{ reward }}
                        </span>
                    </div>
                    <div class="w-full flex">
                        <span>
                            Ratio:
                        </span>
                        <span v-if="valid" class="ml-auto">
                            {{ ratio }} : 1
                        </span>
                    </div>
                    <div class="w-full flex">
                        <span>
                            Risk:
                        </span>
                        <span v-if="valid" class="ml-auto" :class="getUpDownClass(risk)">
                            ${{ risk }}
                        </span>
                    </div>
                </div>

                <div class="w-full p-0 mt-2">
                    <label for="prices">
                        <input id="prices" type="checkbox" name="showPrices" :value="true" v-model="form.showPrices" class="mr-1" />
                        Show Prices
                    </label>
                    <div v-if="form.showPrices" class="flex">
                        <span class="self-center text-sm mr-1">Price Lines:</span>
                        <color-picker v-model="form.color" :initial-color="form.color"></color-picker>
                    </div>
                </div>

                <div class="w-full p-0 mt-2 flex">
                    <span class="self-center text-sm mr-2">Text Color:</span>
                    <color-picker v-model="form.textColor" :initial-color="form.textColor"></color-picker>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="flex justify-center">
                <div v-if="isContext" class="btn btn-sm btn-red inline-flex mr-1 float-left" @click="deleteTool">Delete</div>
                <div class="btn btn-sm btn-yellow inline-flex mx-1" @click="draw">Draw</div>
            </div>
        </template>
    </draggable-modal>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import ColorPicker from './ColorPicker';
import PriceInput from '../PriceInput';
import unitCodeToBaseCode from '@barchart/marketdata-api-js/lib/utilities/convert/unitCodeToBaseCode';

const props = defineProps({
    defaultValues: Object,
    defaultTraits: Object,
    symbol: String,
    // Whether this was opened from the context menu
    isContext: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'pick-point', 'close', 'delete', 'complete']);

const modalRef = ref(null);
const form = ref(getDefaultValues(props.defaultValues, props.defaultTraits));
const entry = ref(props.defaultValues && props.defaultValues.points[0] ? props.defaultValues.points[0] : {});
const stop = ref(props.defaultValues && props.defaultValues.points[1] ? props.defaultValues.points[1] : {});
const target = ref(props.defaultValues && props.defaultValues.points[2] ? props.defaultValues.points[2] : {});
const riskReward = ref(0);
const baseCode = ref(window.Barchart.getFieldFromInlineTemplate('unitCode'));
const symbolType = ref(window.Barchart.getFieldFromInlineTemplate('symbolType'));
const errors = ref({
    entry: '',
    stop: '',
    target: ''
});

const entryRef = ref(null);
const stopRef = ref(null);
const targetRef = ref(null);

// Computed properties
const unitValue = computed(() => {
    if (window.Barchart.getFieldFromInlineTemplate('symbolType') === 10) {
        const unitCode = unitCodeToBaseCode(window.Barchart.getBaseCode());
        // const unitCode = window.Barchart.Utilities.convert.unitCodeToBaseCode(window.Barchart.getBaseCode());
        return Math.pow(10, unitCode);
    }
    return 1;
});

const reward = computed(() => {
    if (valid.value) {
        let reward = form.value.target - form.value.entry;
        if (form.value.longOrShort === 'short') {
            reward *= -1;
        }
        return (reward * form.value.contracts * pointValue.value * unitValue.value).toFixed(2);
    }
});

const risk = computed(() => {
    if (valid.value) {
        let risk = form.value.stop - form.value.entry;
        if (form.value.longOrShort === 'short') {
            risk *= -1;
        }
        return (risk * form.value.contracts * pointValue.value * unitValue.value).toFixed(2);
    }
});

const ratio = computed(() => {
    if (valid.value && risk.value && reward.value) {
        return Math.abs(Math.round((reward.value / risk.value) * 100) / 100);
    }
});

const pointValue = computed(() => {
    return window.Barchart.getFieldFromInlineTemplate('pointValue');
});

const valid = computed(() => {
    return form.value.entry !== ''
        && form.value.stop !== ''
        && form.value.target !== '';
});

// Methods
function handleClose() {
    emit('close');
}

function getDefaultValues(defaultValues, defaultTraits) {
    if (defaultValues) {
        return {
            entry: defaultValues.extras.prices[0],
            stop: defaultValues.extras.prices[1],
            target: defaultValues.extras.prices[2],
            contracts: defaultValues.extras.numContracts,
            longOrShort: defaultValues.extras.pos,
            showPrices: defaultValues.extras.drawLines,
            color: defaultValues.traits.line.color || '#000000',
            textColor: defaultValues.traits.fill.color || '#000000',
            lineThickness: 1,
            lineStyle: 'Solid'
        };
    } else {
        return {
            entry: '',
            stop: '',
            target: '',
            contracts: 1,
            longOrShort: defaultTraits && defaultTraits.longOrShort ? defaultTraits.longOrShort : 'long',
            showPrices: defaultTraits && defaultTraits.line && defaultTraits.line.width ? true : false,
            color: defaultTraits && defaultTraits.line && defaultTraits.line.color ? defaultTraits.line.color : '#000000',
            textColor: defaultTraits && defaultTraits.fill ? defaultTraits.fill.color : '#000000',
            lineThickness: defaultTraits && defaultTraits.line && defaultTraits.line.width ? defaultTraits.line.width : 1,
            lineStyle: 'Solid'
        };
    }
}

function getUpDownClass(value) {
    if (value > 0) {
        return 'text-up';
    } else if (value < 0) {
        return 'text-down';
    }
    return 'text-unch';
}

function setupClickEvent() {
    const charts = window.Barchart.RealtimeWidgets.Charts;
    charts.PubSub.subscribe(charts.Topics.CH_ANNPOINTPICKED, handleChartClick);
}

function pickPoint() {
    emit('pick-point', 'PickPoint');
}

function handleChartClick(eventName, event) {
    if (form.value.entry === '' || entryRef.value.value === '') {
        form.value.entry = event.point.price;
        entry.value = event.point;
        pickPoint();
    } else if (form.value.stop === '' || stopRef.value.value === '') {
        form.value.stop = event.point.price;
        stop.value = event.point;
        pickPoint();
    } else if (form.value.target === '' || targetRef.value.value === '') {
        form.value.target = event.point.price;
        target.value = event.point;
    }
}

function resetErrors() {
    errors.value = {
        entry: '',
        stop: '',
        target: ''
    };
}

function deleteTool() {
    emit('close');
    emit('delete');
}

function validate() {
    let valid = true;

    if (form.value.entry === '') {
        errors.value.entry = 'Please enter an entry value';
        valid = false;
    }

    if (form.value.stop === '') {
        errors.value.stop = 'Please enter a stop value';
        valid = false;
    }

    if (form.value.target === '') {
        errors.value.target = 'Please enter a target value';
        valid = false;
    }

    return valid;
}

function draw() {
    resetErrors();

    if (!validate()) {
        return;
    }

    // If the user entered the values manually, we need to manually set the correct values
    if (!Object.keys(entry.value).length) {
        entry.value = {
            price: form.value.entry,
            time: new Date()
        };
    }

    if (!Object.keys(stop.value).length) {
        stop.value = {
            price: form.value.stop,
            time: new Date()
        };
    }

    if (!Object.keys(target.value).length) {
        target.value = {
            price: form.value.target,
            time: new Date()
        };
    }

    const localTraits = {
        longOrShort: form.value.longOrShort,
        fill: {
            color: form.value.textColor
        }
    };

    if (form.value.showPrices) {
        localTraits.line = {
            color: form.value.color,
            width: form.value.lineThickness,
            dashStyle: form.value.lineStyle
        }
    }

    window.localStorage.setItem('CalculateRiskReward', JSON.stringify(localTraits));

    const entryString = entryRef.value.formatString(form.value.entry);
    const stopString = stopRef.value.formatString(form.value.stop);
    const targetString = targetRef.value.formatString(form.value.target);

    let points = [];
    if (props.defaultValues && props.defaultValues.points && props.defaultValues.points.length === 5) {
        points = props.defaultValues.points;

        points[2].price = form.value.entry;
        points[3].price = form.value.stop;
        points[4].price = form.value.target;
    } else {
        points = [
            entry.value,
            stop.value,
            target.value
        ];
    }

    emit('complete', 'ComputedText',
        {
            line: {
                color: form.value.color,
                width: form.value.lineThickness,
                dashStyle: form.value.lineStyle,
            },
            fill: {
                color: form.value.textColor
            }
        },
        {
            points,
            prices: [
                form.value.entry,
                form.value.stop,
                form.value.target
            ],
            drawLines: form.value.showPrices,
            textLines: [
                `Entry Price: ${entryString}`,
                `Stop Loss: ${stopString}`,
                `Target Price: ${targetString}`,
                `# Contracts: ${form.value.contracts}`,
                form.value.longOrShort === 'short' ? 'Short' : 'Long',
                `Reward: $${reward.value}`,
                `Risk: $${risk.value}`,
                `Ratio: ${ratio.value} : 1`,
            ],
            kind: 'RewardRisk',
            numContracts: form.value.contracts,
            pos: form.value.longOrShort
        },
        points
    )

    emit('close');
}

onMounted(() => {
    setupClickEvent();
});

onBeforeUnmount(() => {
    const charts = window.Barchart.RealtimeWidgets.Charts;
    charts.PubSub.unsubscribe(charts.Topics.CH_ANNPOINTPICKED, handleChartClick);
});
</script>

<style lang="scss">
#modalToolRiskReward {
    .modal-align {
        left: 50%;
        margin-left: -13rem;
        @screen xs {
            left: auto;
            margin-left: 0;
        }
    }
    .modal-container {
        width: 24rem;
        @screen xs {
            width: 100%;
        }
    }
}
</style>
